import { HttpEventType } from "@angular/common/http";
import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { SafeResourceUrl } from "@angular/platform-browser";

import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from "rxjs";

import { FileService } from "@bpm/services";
import { AuthenticationService, AuthenticationTypeService } from "@common/services";
import { InsightsService } from "@core/services";
import { fileVisualizeUrl } from "@core/utils/file-url";
import { exitFullscreen, fullscreenElement, requestFullscreen } from "@core/utils";
import { RepositoryService } from "@repositories/services";
import { environment } from "environments/environment";

/**
 * Archivo del gestor que se muestra en el visor
 */
export interface FileViewerItem {
    /** Nombre con el que se ve y se descarga */
    documentName: string;
    /** Nombre del archivo en el storage de bpm, o URL absoluta si `isExternal` */
    documentUrl: string;
    /** El archivo no está en el storage: se lee directo de `documentUrl` */
    isExternal?: boolean;
}

/**
 * Muestra un archivo del gestor de archivos en un modal, con el mismo visor
 * que belivpedia (`RepositoryViewer`): el tipo y la URL salen de
 * `RepositoryService.getUrlForViewer` y cada tipo se pinta igual que allá.
 */
@Component({
    selector: "app-file-viewer-modal",
    templateUrl: "./file-viewer-modal.component.html",
    styleUrls: ["./file-viewer-modal.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FileViewerModalComponent implements OnInit, OnDestroy {

    @Input() file: FileViewerItem;
    /** Contexto del gestor que lo abrió, para las trazas */
    @Input() context: { menu?: string; typeProcess?: string } = {};

    public fileType: string;
    public url: string | SafeResourceUrl;
    public textFileContent: string;
    public hasError: boolean = false;
    /** Progreso de la descarga (0-100); null si el servidor no informa el tamaño */
    public loadingPercent: number | null = 0;
    /** El modal ocupa toda la pantalla */
    public isFullscreen: boolean = false;

    private _blob: Blob;
    private _objectUrl: string;
    /** Descarga en curso, para cortarla si se cierra el modal antes de que termine */
    private _download: Subscription;
    private _abort: AbortController;

    /**
     * Constructor
     * @param modal
     * @param _fileService
     * @param _repositoryService
     * @param _authenticationTypeService
     * @param _authenticationService
     * @param _insightsService
     * @param _cdr
     */
    constructor(
        public modal: NgbActiveModal,
        private _fileService: FileService,
        private _repositoryService: RepositoryService,
        private _authenticationTypeService: AuthenticationTypeService,
        private _authenticationService: AuthenticationService,
        private _insightsService: InsightsService,
        private _cdr: ChangeDetectorRef,
        private _elementRef: ElementRef<HTMLElement>
    ) { }

    /**
     * OnInit
     */
    ngOnInit(): void {
        this.loadFile();
    }

    /**
     * OnDestroy
     */
    ngOnDestroy(): void {
        if (fullscreenElement()) exitFullscreen();
        this._download?.unsubscribe();
        this._abort?.abort();
        if (this._objectUrl) window.URL.revokeObjectURL(this._objectUrl);
    }

    /**
     * El visor de Office no siempre puede con el archivo: se avisa y se ofrece la descarga
     */
    get isDocument(): boolean {
        return this.fileType === "document";
    }

    /**
     * Descarga el archivo que ya se cargó para el visor, sin volver a pedirlo
     */
    download(): void {
        if (!this._objectUrl) return;

        const a = document.createElement("a");
        a.href = this._objectUrl;
        a.download = this.file.documentName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        this.log("Descargó un archivo desde el visor", { documentName: this.file.documentName, fileType: this.fileType });
    }

    /**
     * Pasa todo el modal (header, archivo y footer) a pantalla completa, así
     * las acciones y el botón para salir siguen a la vista. Con Esc el
     * navegador sale solo y el estado se sincroniza en onFullscreenChange.
     */
    toggleFullscreen(): void {
        if (fullscreenElement()) {
            exitFullscreen();
        } else {
            requestFullscreen(this._elementRef.nativeElement.closest(".modal-content"));
        }

        this.log(this.isFullscreen ? "Salió de pantalla completa en el visor" : "Abrió el visor en pantalla completa",
            { documentName: this.file.documentName, fileType: this.fileType });
    }

    /** El navegador también sale de pantalla completa por su cuenta (Esc) */
    @HostListener("document:fullscreenchange")
    @HostListener("document:webkitfullscreenchange")
    onFullscreenChange(): void {
        this.isFullscreen = !!fullscreenElement();
        this._cdr.detectChanges();
    }

    /**
     * Cierra el visor
     */
    cancel(): void {
        this.log("Cerró el visor de archivos", { documentName: this.file.documentName, fileType: this.fileType });
        this.modal.dismiss();
    }

    /**
     * Baja el archivo y arma la URL que necesita cada visor
     */
    private async loadFile(): Promise<void> {
        try {
            // Nombre sin query string: de acá sale la extensión que decide el visor
            const storageName = this.file.documentUrl.split("?")[0];

            this._blob = this.file.isExternal
                ? await this.fetchExternal(this.file.documentUrl)
                : await this.downloadFromStorage(this.file.documentUrl);

            this._objectUrl = window.URL.createObjectURL(this._blob);

            // El visor de Office lee el archivo desde una URL pública
            const visualizationUrl = this.file.isExternal
                ? this.file.documentUrl
                : fileVisualizeUrl(storageName, (await this._authenticationTypeService.getApiKey()) as string);

            const viewer = await this._repositoryService.getUrlForViewer(storageName, this._objectUrl, this._blob, visualizationUrl);

            if (viewer.fileType === "txt") {
                this.textFileContent = await this._blob.text();
            }

            this.url = viewer.url;
            this.fileType = viewer.fileType;
        } catch (error) {
            this.hasError = true;
            this.log("Error al cargar un archivo en el visor", { documentName: this.file?.documentName }, error, true);
        }

        this._cdr.detectChanges();
    }

    /**
     * Baja el archivo del storage de bpm informando el progreso
     * @param fileName Nombre del archivo en el storage
     */
    private downloadFromStorage(fileName: string): Promise<Blob> {
        return new Promise((resolve, reject) => {
            this._download = this._fileService.downloadBlobWithProgress(fileName).subscribe({
                next: event => {
                    if (event.type === HttpEventType.DownloadProgress) {
                        this.setProgress(event.loaded, event.total);
                    } else if (event.type === HttpEventType.Response) {
                        resolve(event.body);
                    }
                },
                error: reject
            });
        });
    }

    /**
     * Baja un archivo desde una URL absoluta leyendo el stream, para poder
     * informar el progreso
     * @param url URL del archivo
     */
    private async fetchExternal(url: string): Promise<Blob> {
        this._abort = new AbortController();
        const response = await fetch(url, { signal: this._abort.signal });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // Sin stream legible (navegadores viejos) no hay progreso que mostrar
        if (!response.body) return response.blob();

        const total = Number(response.headers.get("content-length")) || 0;
        const reader = response.body.getReader();
        const chunks: BlobPart[] = [];
        let loaded = 0;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value as BlobPart);
            loaded += value.length;
            this.setProgress(loaded, total);
        }

        return new Blob(chunks, { type: response.headers.get("content-type") ?? "" });
    }

    /**
     * Actualiza la barra. Si no se conoce el total queda indeterminada.
     * @param loaded Bytes recibidos
     * @param total Bytes totales (0 o undefined si el servidor no lo informa)
     */
    private setProgress(loaded: number, total?: number): void {
        this.loadingPercent = total ? Math.min(100, Math.round((100 * loaded) / total)) : null;
        this._cdr.detectChanges();
    }

    /**
     * Manda una traza a Application Insights, etiquetada con el proyecto y el
     * usuario que la originó, más el menú desde el que se usa el gestor.
     */
    private log(message: string, data: any, ex: any = null, error: boolean = false): void {
        const user = this._authenticationService.currentUserValue;
        const payload = { ...data, menu: this.context?.menu ?? null, typeProcess: this.context?.typeProcess ?? null };

        if (error) {
            this._insightsService.traceError(environment.projects.FileManager, message, payload, ex, user);
            return;
        }

        this._insightsService.traceInfo(environment.projects.FileManager, message, payload, user);
    }
}
